/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { Patient, PatientGender } from '../backendModels/patient.model';
import { PatientDataRecord, PatientPropertyKeys } from './genericElements/patientData';
import dayjs from 'dayjs';
import {
  BirthDateBackendFormat,
  BirthDateUiFormat,
  ExtendedPatientDataRecord,
  genderLabels,
  getAgeFromBirthday,
  NewBornAgeKeys,
  newBornAgeLabels,
} from './genericElements/extendedPatientData';
import { SymptomOnsetRecord } from './genericElements/operationalData/symptomOnset';

export interface NormalizedPatient {
  name?: string | null;
  surname?: string | null;
  age?: number | null;
  newbornAge?: NewBornAgeKeys | null;
  birthday?: string | null;
  city?: string | null;
  gender?: PatientGender | null;
  insuranceName?: string | null;
  insuranceNumber?: string | null;
  patientId?: string | null;
  policyNumber?: string | null;
  postalCode?: string | null;
  street?: string | null;
  symptomOnset?: string | null;
}

export function normalizePatient(patient?: Patient): NormalizedPatient {
  const birthday = patient?.birthday != null ? dayjs(patient.birthday, BirthDateBackendFormat) : null;
  const [age, newbornAge] = getAgeFromBirthday(birthday);

  return {
    name: patient?.name ?? null,
    surname: patient?.surname ?? null,
    age: age ?? patient?.age ?? null,
    newbornAge: newbornAge,
    birthday: patient?.birthday ?? null,
    city: patient?.city ?? null,
    gender: patient?.gender ?? null,
    insuranceName: patient?.insuranceName ?? null,
    insuranceNumber: patient?.insuranceNumber ?? null,
    patientId: patient?.patientId ?? null,
    policyNumber: patient?.policyNumber ?? null,
    postalCode: patient?.postalCode ?? null,
    street: patient?.street ?? null,
    symptomOnset: patient?.symptomOnset ?? null,
  };
}

export const patientPropertiesToLabels: Record<keyof NormalizedPatient, string> = {
  name: 'Vorname',
  surname: 'Name',
  age: 'Alter',
  newbornAge: 'Neugeborenen Alter',
  birthday: 'Geburtsdatum',
  city: 'Ort',
  gender: 'Geschlecht',
  street: 'Straße + Hausnummer',
  postalCode: 'PLZ',
  patientId: 'Patienten Nr.',
  insuranceName: 'Kasse',
  insuranceNumber: 'Kasse / Nr.',
  policyNumber: 'Versichertennummer',
  symptomOnset: 'Symptom Beginn (z.B. Kollaps Unfallzeitpunkt)',
};

type PatientRecords = {
  patientRecord?: PatientDataRecord;
  extendedPatientRecord?: ExtendedPatientDataRecord;
  symptomOnsetRecord?: SymptomOnsetRecord;
};

export function localizePatientPropertyTuple(
  propertyName: keyof NormalizedPatient,
  propertyValue: string | number | null,
  records: PatientRecords
): [string, string | null, string | null] {
  const { patientRecord, extendedPatientRecord, symptomOnsetRecord } = records;

  let oldValue: string | number | null | undefined;

  if (propertyName === 'age') {
    oldValue = patientRecord?.values?.[propertyName]?.number;
  } else if (propertyName === 'gender') {
    oldValue = patientRecord?.values?.[propertyName]?.option;
  } else if (propertyName === 'newbornAge') {
    oldValue = extendedPatientRecord?.values?.[propertyName]?.option;
  } else if (propertyName === 'symptomOnset') {
    oldValue = symptomOnsetRecord?.values?.symptomOnsetTimestamp?.text;
  } else {
    oldValue = patientRecord?.values?.[propertyName]?.text;
  }

  return [
    patientPropertiesToLabels[propertyName],
    localizePatientPropertyValue(propertyName, propertyValue),
    localizePatientPropertyValue(propertyName, oldValue),
  ];
}

function localizePatientPropertyValue(
  propertyName: string,
  propertyValue: string | number | null | undefined
): string | null {
  if (propertyValue == null) {
    return null;
  }

  if (propertyName === 'newbornAge') {
    return newBornAgeLabels[propertyValue as NewBornAgeKeys];
  } else if (propertyName === 'gender') {
    return genderLabels[propertyValue as PatientGender];
  } else if (propertyName === PatientPropertyKeys.birthday) {
    return dayjs(propertyValue, BirthDateBackendFormat).format(BirthDateUiFormat);
  }

  return propertyValue?.toString();
}
